// PartnerStatusTab — Team N (2026-05-11)
//
// 学習機構 ページ内 「業者別」 タブ。
// 業者一覧 table + filter (category / template_status / sort) + KPI cards +
// drill-down side panel (PartnerStatusDrillDown)。
//
// API:
//   GET /api/admin/partner-learning-status      (一覧 + KPI + reader perf)
//   GET /api/admin/partner-learning-detail/:id  (drill-down)

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CardBase,
  Note,
  Button,
  MessageBlock,
  SubSectionTitle,
  SelectBox,
  InlineSpinner,
  TextField,
} from '@freee_jp/vibes';
import {
  MdGroups,
  MdSchool,
  MdCheckCircle,
  MdWarning,
  MdInfo,
  MdTrendingUp,
  MdRefresh,
  MdRestaurant,
  MdBuild,
  MdLightbulb,
} from 'react-icons/md';
import {
  fetchPartnerLearningStatus,
  PartnerLearningRow,
  PartnerLearningStatusResponse,
  PartnerLearningStatusParams,
} from '../../api/client';
import { PartnerStatusTable } from './PartnerStatusTable';
import { PartnerStatusDrillDown } from './PartnerStatusDrillDown';

const fmtPct = (n: number | null | undefined): string =>
  n == null ? '—' : `${(n * 100).toFixed(0)}%`;

export const PartnerStatusTab: React.FC = () => {
  const [data, setData] = useState<PartnerLearningStatusResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [category, setCategory] = useState<string>('all');
  const [status, setStatus] = useState<string>('all');
  const [sort, setSort] = useState<string>('deal_count');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [nameQuery, setNameQuery] = useState<string>('');

  // Drill-down
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: PartnerLearningStatusParams = {
        category: category === 'all' ? undefined : category,
        status: status === 'all' ? undefined : (status as any),
        sort: sort as any,
        order,
        limit: 500,
      };
      const r = await fetchPartnerLearningStatus(params);
      setData(r);
    } catch (e: any) {
      setError(e?.message || String(e));
    }
    setLoading(false);
  }, [category, status, sort, order]);

  useEffect(() => { load(); }, [load]);

  // 部分一致 filter (= client-side, name で絞り込み)
  const filteredPartners = useMemo((): PartnerLearningRow[] => {
    if (!data?.partners) return [];
    if (!nameQuery.trim()) return data.partners;
    const q = nameQuery.trim().toLowerCase();
    return data.partners.filter(p =>
      (p.partner_name || '').toLowerCase().includes(q) ||
      String(p.partner_id).includes(q)
    );
  }, [data, nameQuery]);

  return (
    <>
      <Note>
        partner_invoice_layout_templates + partner_curated_patterns + partner_details_cache + invoice_accounting_audit 統合 dashboard。
        各業者の学習進捗 (promoted / draft / 未学習) + canonical 適合率 + amount paradigm を一覧。
      </Note>
      {error && <MessageBlock error mb={0.5}>{error}</MessageBlock>}

      {/* KPI Top Cards */}
      <CardBase paddingSize="small" mb={0.5}>
        <div style={{ padding: '12px', display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'center' }}>
          <Kpi icon={<MdGroups />} label="全業者" value={String(data?.kpi.total_partners ?? 0)}
               sub={data ? `template 有 ${data.kpi.with_template}` : ''} color="#2e7cf6" />
          <Kpi icon={<MdCheckCircle />} label="promoted" value={String(data?.kpi.promoted ?? 0)}
               sub={data && data.kpi.with_template > 0 ? `${((data.kpi.promoted / data.kpi.with_template) * 100).toFixed(0)}% of templates` : ''}
               color="#338650" />
          <Kpi icon={<MdSchool />} label="draft (学習中)" value={String(data?.kpi.draft ?? 0)}
               sub={data ? `${data.kpi.draft} 件 検証待ち` : ''} color="#7c5cff" />
          <Kpi icon={<MdWarning />} label="demoted" value={String(data?.kpi.demoted ?? 0)}
               sub={data && data.kpi.demoted > 0 ? '要再学習' : '降格なし'}
               color={data && data.kpi.demoted > 0 ? '#c33939' : '#8a8580'} />
          <Kpi icon={<MdRestaurant />} label="食材系" value={String(data?.kpi.food_vendor ?? 0)}
               sub="food_vendor" color="#dd7a00" />
          <Kpi icon={<MdLightbulb />} label="光熱系" value={String(data?.kpi.utility ?? 0)}
               sub="utility" color="#2e7cf6" />
          <Kpi icon={<MdBuild />} label="修繕系" value={String(data?.kpi.repair ?? 0)}
               sub="repair" color="#7c5cff" />
          <Kpi icon={<MdTrendingUp />} label="canonical 平均適合率" value={fmtPct(data?.kpi.avg_canonical_match)}
               sub="dominant vs curated" color="#338650" />
          <Kpi icon={<MdInfo />} label="curated 記録" value={String(data?.kpi.curated_present ?? 0)}
               sub="partner-patterns.md" color="#7c5cff" />
          {loading && <InlineSpinner isLoading={true} />}
        </div>
      </CardBase>

      {/* Reader Performance */}
      <CardBase paddingSize="small" mb={0.5}>
        <div style={{ padding: '12px' }}>
          <SubSectionTitle headlineLevel={4}>4-reader Performance (invoice_reader_performance)</SubSectionTitle>
          <Note mt={0.25}>各 PDF reader の重み付け / 検証履歴 (= post_verified_correct = approved 後の正解数)</Note>
          <table style={{ width: '100%', marginTop: '8px', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
            <thead>
              <tr style={{ background: '#f7f5f5', textAlign: 'left' }}>
                <th style={th}>reader</th>
                <th style={{ ...th, textAlign: 'right' }}>total_runs</th>
                <th style={{ ...th, textAlign: 'right' }}>correct</th>
                <th style={{ ...th, textAlign: 'right' }}>wrong</th>
                <th style={{ ...th, textAlign: 'right' }}>weight</th>
                <th style={th}>last_updated</th>
              </tr>
            </thead>
            <tbody>
              {(data?.reader_performance || []).map(r => (
                <tr key={r.reader_type} style={{ borderBottom: '1px solid #f0eeec' }}>
                  <td style={{ ...td, fontWeight: 600 }}>{r.reader_type}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{r.total_runs}</td>
                  <td style={{ ...td, textAlign: 'right', color: '#338650' }}>{r.post_verified_correct}</td>
                  <td style={{ ...td, textAlign: 'right', color: r.post_verified_wrong > 0 ? '#c33939' : '#8a8580' }}>{r.post_verified_wrong}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: r.current_weight >= 0.85 ? '#338650' : r.current_weight >= 0.5 ? '#7c5cff' : '#dd7a00' }}>
                    {r.current_weight.toFixed(2)}
                  </td>
                  <td style={{ ...td, fontSize: '0.6875rem' }}>{r.last_updated || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardBase>

      {/* Filters */}
      <CardBase paddingSize="small" mb={0.5}>
        <div style={{ padding: '12px' }}>
          <SubSectionTitle headlineLevel={4}>Filter / Sort</SubSectionTitle>
          <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
            <FilterCell label="category">
              <SelectBox
                options={[
                  { name: 'すべて', value: 'all' },
                  { name: '食材 (food_vendor)', value: 'food_vendor' },
                  { name: '光熱 (utility)', value: 'utility' },
                  { name: '修繕 (repair)', value: 'repair' },
                  { name: '害虫 (pest_control)', value: 'pest_control' },
                  { name: '消耗品 (consumable)', value: 'consumable' },
                ]}
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              />
            </FilterCell>
            <FilterCell label="template status">
              <SelectBox
                options={[
                  { name: 'すべて', value: 'all' },
                  { name: 'promoted (確定)', value: 'promoted' },
                  { name: 'draft (学習中)', value: 'draft' },
                  { name: 'demoted (降格)', value: 'demoted' },
                  { name: '未学習 (no_template)', value: 'no_template' },
                ]}
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              />
            </FilterCell>
            <FilterCell label="sort">
              <SelectBox
                options={[
                  { name: '取引数 (deal_count)', value: 'deal_count' },
                  { name: 'AI 信頼度 (confidence)', value: 'confidence' },
                  { name: 'sample 数', value: 'sample' },
                  { name: '名前', value: 'name' },
                ]}
                value={sort}
                onChange={(e) => setSort(e.target.value)}
              />
            </FilterCell>
            <FilterCell label="order">
              <SelectBox
                options={[
                  { name: '降順 (desc)', value: 'desc' },
                  { name: '昇順 (asc)', value: 'asc' },
                ]}
                value={order}
                onChange={(e) => setOrder(e.target.value as any)}
              />
            </FilterCell>
            <FilterCell label="名前/id 検索">
              <TextField value={nameQuery} onChange={(e) => setNameQuery(e.target.value)} placeholder="例: 尾家産業" />
            </FilterCell>
            <Button onClick={load} small>
              <MdRefresh style={{ verticalAlign: 'middle', marginRight: 4 }} />
              再取得
            </Button>
          </div>
        </div>
      </CardBase>

      {/* Table */}
      <CardBase paddingSize="small" mb={0.5}>
        <div style={{ padding: '12px 0px 0px 0px' }}>
          <div style={{ padding: '0 12px 8px' }}>
            <SubSectionTitle headlineLevel={4}>
              業者一覧 ({filteredPartners.length} / {data?.kpi.total_partners ?? 0} 件)
            </SubSectionTitle>
            <Note mt={0.25}>行をクリックで drill-down 詳細</Note>
          </div>
          <PartnerStatusTable
            partners={filteredPartners}
            selectedPartnerId={selectedPartnerId}
            onSelect={setSelectedPartnerId}
          />
        </div>
      </CardBase>

      {/* Drill-down */}
      {selectedPartnerId && (
        <PartnerStatusDrillDown
          partnerId={selectedPartnerId}
          onClose={() => setSelectedPartnerId(null)}
        />
      )}
    </>
  );
};

const th: React.CSSProperties = { padding: '6px 8px', fontSize: '0.6875rem', color: '#6b6560', fontWeight: 500 };
const td: React.CSSProperties = { padding: '6px 8px', verticalAlign: 'top' };

const Kpi: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  color: string;
}> = ({ icon, label, value, sub, color }) => (
  <div style={{ display: 'flex', flexDirection: 'column', minWidth: '120px' }}>
    <div style={{ fontSize: '0.6875rem', color: '#8a8580', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px' }}>
      <span style={{ color, fontSize: '1rem', display: 'inline-flex' }}>{icon}</span>
      {label}
    </div>
    <div style={{ fontSize: '1.125rem', fontWeight: 600, color }}>{value}</div>
    {sub && <div style={{ fontSize: '0.6875rem', color: '#8a8580' }}>{sub}</div>}
  </div>
);

const FilterCell: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '160px' }}>
    <div style={{ fontSize: '0.6875rem', color: '#8a8580', fontWeight: 500 }}>{label}</div>
    {children}
  </div>
);
